import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi, type ChannelCloseoutWriteRequest } from '@/shared/api';
import { suggestCloseoutName } from './channelCloseoutPresentation';
import { draftKey, emptyCloseoutDraft, isMappedInAll } from './channelCloseoutDraft';
import { useChannelCloseoutOptions } from './useChannelCloseoutOptions';
import { useCloseoutPreview } from './useCloseoutPreview';

async function showProblem(error: unknown) {
  const problem = await toApiError(error);
  toast.error(problem.detail ?? problem.title);
}

/** Die Leiste wird je Öffnung neu gemountet; der Zustand beginnt also immer frisch. */
export function useChannelCloseoutEditor({
  propertyGroupId,
  closeoutId,
  onSaved,
}: {
  propertyGroupId: string;
  closeoutId: string | null;
  onSaved: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<ChannelCloseoutWriteRequest>(emptyCloseoutDraft);
  // Solange niemand tippt, folgt die Bezeichnung dem Vorschlag aus Kanal und Zeitraum.
  const [nameTouched, setNameTouched] = useState(false);
  // Die geladene Fassung. Gespeichert wird gegen ihre Revision: ein späteres Nachladen der Regel
  // darf weder Eingaben überschreiben noch eine fremde Änderung stillschweigend übergehen.
  const [base, setBase] = useState<{ revision: number; key: string } | null>(null);
  const [minStayApartmentId, setMinStayApartmentId] = useState('');
  const { options, problem } = useChannelCloseoutOptions(propertyGroupId, true);
  const detail = useQuery({
    queryKey: ['availability-distribution', 'closeout', propertyGroupId, closeoutId],
    queryFn: () => api.availabilityDistribution.closeouts.get(propertyGroupId, closeoutId!),
    enabled: Boolean(closeoutId),
  });
  const loaded = detail.data;
  useEffect(() => {
    if (!loaded || base) return;
    const desired = {
      name: loaded.closeout.name,
      startDate: loaded.closeout.startDate,
      endDate: loaded.closeout.endDate,
      propertyIds: loaded.propertyIds,
      channels: loaded.closeout.channels,
    };
    setValues(desired);
    setNameTouched(true);
    setBase({ revision: loaded.closeout.desiredRevision, key: draftKey(desired) });
    setMinStayApartmentId(loaded.propertyIds[0] ?? '');
  }, [base, loaded]);
  const apartments = useMemo(() => {
    const known = options.data?.apartments ?? [];
    // Ein inzwischen fehlendes Apartment bleibt sichtbar, damit man es aus der Regel nehmen kann.
    const gone = (loaded?.closeout.apartments ?? [])
      .filter((item) => !known.some((apartment) => apartment.propertyId === item.propertyId))
      .map((item) => ({ ...item, channelCodes: [] }));
    return [...known, ...gone];
  }, [loaded, options.data]);
  const name = nameTouched
    ? values.name
    : suggestCloseoutName(values.channels, values.startDate, values.endDate);
  const draft = useMemo(() => ({ ...values, name }), [name, values]);
  const ready =
    name.trim().length > 0 &&
    Boolean(draft.startDate && draft.endDate) &&
    draft.propertyIds.length > 0 &&
    draft.channels.length > 0;
  const check = useCloseoutPreview(propertyGroupId, draft, ready);
  const changed = !base || draftKey(draft) !== base.key;
  // Was beim Schließen verloren ginge: beim Bearbeiten jede Änderung, beim Anlegen jede Eingabe.
  const dirty = base ? changed : draftKey(values) !== draftKey(emptyCloseoutDraft);
  const save = useMutation({
    mutationFn: () =>
      closeoutId
        ? api.availabilityDistribution.closeouts.update(propertyGroupId, closeoutId, {
            expectedRevision: base!.revision,
            desired: draft,
          })
        : api.availabilityDistribution.closeouts.create(propertyGroupId, draft),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['availability-distribution'] });
      toast.success(closeoutId ? 'Änderung vorgemerkt' : 'Kanalregel vorgemerkt');
      onSaved();
    },
    onError: showProblem,
  });
  const toggleChannel = (code: string) =>
    setValues((current) => {
      const channels = current.channels.includes(code)
        ? current.channels.filter((item) => item !== code)
        : [...current.channels, code];
      // Ein Apartment, das im neuen Kanal nicht gemappt ist, lehnte der Server ohnehin ab.
      const propertyIds = current.propertyIds.filter((id) =>
        isMappedInAll(apartments, id, channels),
      );
      return { ...current, channels, propertyIds };
    });
  const toggleApartment = (id: string) =>
    setValues((current) => ({
      ...current,
      propertyIds: current.propertyIds.includes(id)
        ? current.propertyIds.filter((item) => item !== id)
        : [...current.propertyIds, id],
    }));
  return {
    draft,
    apartments,
    options,
    optionsProblem: problem,
    detail,
    check,
    save,
    ready,
    toggleChannel,
    toggleApartment,
    setPeriod: (from: string, to: string) =>
      setValues((current) => ({ ...current, startDate: from, endDate: to })),
    setName: (next: string) => {
      setNameTouched(true);
      setValues((current) => ({ ...current, name: next }));
    },
    nameSuggested: !nameTouched && name.length > 0,
    dirty,
    minStayApartmentId,
    setMinStayApartmentId,
    loading: options.isLoading || detail.isLoading,
    canSave: ready && changed && check.result !== null && !save.isPending,
  };
}
